import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Search, ArrowLeft, MessageCircle, Check, CheckCheck, Loader2 } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import api from '../../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

// ── Utilitários ───────────────────────────────────────────────
function tempoRelativo(data) {
  if (!data) return '';
  const d = new Date(data), agora = new Date(), diff = agora - d;
  if (diff < 60000)    return 'agora';
  if (diff < 3600000)  return `${Math.floor(diff/60000)}min`;
  if (diff < 86400000) return d.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
  if (diff < 604800000)return d.toLocaleDateString('pt-BR', { weekday:'short' });
  return d.toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit' });
}
function horaMsg(data) {
  return new Date(data).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
}
function isOnline(ultimo) {
  if (!ultimo) return false;
  return (new Date() - new Date(ultimo)) < 5 * 60 * 1000;
}

// ── Avatar ────────────────────────────────────────────────────
function Avatar({ nome, src, online, size = 'md' }) {
  const [err, setErr] = useState(false);
  const ini = nome?.charAt(0).toUpperCase() || '?';
  const sz  = size === 'sm' ? 'w-9 h-9 text-sm' : size === 'lg' ? 'w-14 h-14 text-xl' : 'w-11 h-11 text-base';
  return (
    <div className="relative flex-shrink-0">
      {(src && !err)
        ? <img src={src.startsWith('http') ? src : `${API_BASE}${src}`} alt={nome}
            className={`${sz} rounded-full object-cover`} onError={() => setErr(true)}/>
        : <div className={`${sz} rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold`}>
            {ini}
          </div>}
      {online !== undefined && (
        <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? 'bg-green-500' : 'bg-gray-400'}`}/>
      )}
    </div>
  );
}

// ── Item da lista de conversas ────────────────────────────────
function ConvItem({ conv, ativo, meuId, onClick }) {
  const naoLida = Number(conv.nao_lidas) > 0;
  const online  = isOnline(conv.outro_ativo_em);
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 transition-colors text-left ${ativo ? 'bg-[#f0f2f5]' : 'hover:bg-[#f5f6f6]'}`}>
      <Avatar nome={conv.outro_nome} src={conv.outro_avatar} online={online} size="md"/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className={`text-sm truncate ${naoLida ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
            {conv.outro_nome}
          </span>
          <span className={`text-xs flex-shrink-0 ml-2 ${naoLida ? 'text-[#25D366] font-semibold' : 'text-gray-400'}`}>
            {tempoRelativo(conv.ultima_msg_at)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <p className={`text-xs truncate flex-1 ${naoLida ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
            {conv.ultima_msg_autor_id === meuId && <span className="text-gray-400 mr-1">✓</span>}
            {conv.ultima_msg || <em className="text-gray-400">Iniciar conversa</em>}
          </p>
          {naoLida && (
            <span className="ml-2 bg-[#25D366] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center flex-shrink-0">
              {conv.nao_lidas > 9 ? '9+' : conv.nao_lidas}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Bolha de mensagem ─────────────────────────────────────────
function Bolha({ msg, isMinha }) {
  return (
    <div className={`flex ${isMinha ? 'justify-end' : 'justify-start'} mb-1.5`}>
      <div className={`max-w-[72%] min-w-[60px] px-3 py-2 rounded-2xl shadow-sm relative ${
        isMinha ? 'bg-[#dcf8c6] rounded-tr-none' : 'bg-white rounded-tl-none'
      }`}>
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">{msg.conteudo}</p>
        <div className="flex items-center justify-end gap-1 mt-0.5">
          <span className="text-[10px] text-gray-400">{horaMsg(msg.created_at)}</span>
          {isMinha && (msg.lida
            ? <CheckCheck size={12} className="text-blue-500"/>
            : <Check size={12} className="text-gray-400"/>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Separador de data ─────────────────────────────────────────
function SepData({ data }) {
  const d = new Date(data), hoje = new Date(), ontem = new Date();
  ontem.setDate(hoje.getDate()-1);
  const label = d.toDateString() === hoje.toDateString() ? 'Hoje'
    : d.toDateString() === ontem.toDateString() ? 'Ontem'
    : d.toLocaleDateString('pt-BR', { day:'2-digit', month:'long', year:'numeric' });
  return (
    <div className="flex justify-center my-3">
      <span className="bg-white/90 border border-gray-200 text-gray-500 text-xs px-3 py-1 rounded-full shadow-sm">{label}</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function Chat() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [conversas,    setConversas]    = useState([]);
  const [convAtiva,    setConvAtiva]    = useState(null);
  const [mensagens,    setMensagens]    = useState([]);
  const [texto,        setTexto]        = useState('');
  const [busca,        setBusca]        = useState('');
  const [enviando,     setEnviando]     = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [loadMsg,      setLoadMsg]      = useState(false);

  const msgEndRef  = useRef(null);
  const inputRef   = useRef(null);
  const pollConvRef = useRef(null);
  const pollMsgRef  = useRef(null);

  // Detecta mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLista, setShowLista] = useState(true);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Carregar conversas ────────────────────────────────────
  const carregarConversas = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/chat/conversas');
      setConversas(data || []);
    } catch {}
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    carregarConversas();
    pollConvRef.current = setInterval(() => carregarConversas(true), 8000);
    return () => clearInterval(pollConvRef.current);
  }, [carregarConversas]);

  // ── Carregar mensagens ────────────────────────────────────
  const carregarMensagens = useCallback(async (convId, silent = false) => {
    if (!convId) return;
    if (!silent) setLoadMsg(true);
    try {
      const { data } = await api.get(`/chat/conversas/${convId}/mensagens`);
      setMensagens(data || []);
      carregarConversas(true);
    } catch {}
    if (!silent) setLoadMsg(false);
  }, [carregarConversas]);

  // ── Abrir conversa ────────────────────────────────────────
  const abrirConversa = useCallback((conv) => {
    setConvAtiva(conv);
    setMensagens([]);
    carregarMensagens(conv.id);
    if (isMobile) setShowLista(false);
    clearInterval(pollMsgRef.current);
    pollMsgRef.current = setInterval(() => carregarMensagens(conv.id, true), 3000);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [carregarMensagens, isMobile]);

  // Atualizar referência da conversa ativa quando conversas mudam
  useEffect(() => {
    if (convAtiva) {
      const atualizada = conversas.find(c => c.id === convAtiva.id);
      if (atualizada) setConvAtiva(atualizada);
    }
  }, [conversas]);

  // Cleanup polling
  useEffect(() => () => {
    clearInterval(pollConvRef.current);
    clearInterval(pollMsgRef.current);
  }, []);

  // Auto-scroll
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  // ── Enviar mensagem ────────────────────────────────────────
  const enviar = async (e) => {
    e?.preventDefault();
    if (!texto.trim() || !convAtiva || enviando) return;
    const conteudo = texto.trim();
    setTexto('');
    setEnviando(true);

    // Mensagem otimista
    const tmpMsg = {
      id: 'tmp-' + Date.now(),
      conversa_id: convAtiva.id,
      autor_id: user.id,
      conteudo,
      lida: false,
      created_at: new Date().toISOString(),
    };
    setMensagens(prev => [...prev, tmpMsg]);

    try {
      await api.post(`/chat/conversas/${convAtiva.id}/mensagens`, { conteudo });
      // Recarregar mensagens reais imediatamente
      await carregarMensagens(convAtiva.id, true);
    } catch {
      setMensagens(prev => prev.filter(m => m.id !== tmpMsg.id));
      setTexto(conteudo);
    }
    setEnviando(false);
    inputRef.current?.focus();
  };

  // Agrupar msgs por data
  function msgsPorDia(msgs) {
    const g = []; let dia = null;
    msgs.forEach(m => {
      const d = new Date(m.created_at).toDateString();
      if (d !== dia) { g.push({ tipo:'data', data:m.created_at }); dia=d; }
      g.push({ tipo:'msg', msg:m });
    });
    return g;
  }

  const convsFiltradas = conversas.filter(c =>
    c.outro_nome?.toLowerCase().includes(busca.toLowerCase())
  );

  // Fundo estilo WhatsApp
  const wpBg = "url(\"data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100' height='100' fill='%23e5ddd5'/%3E%3C/svg%3E\")";

  return (
    <div className="h-screen flex overflow-hidden bg-[#f0f2f5]" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* ════ SIDEBAR ════ */}
      <div className={`${isMobile ? (showLista ? 'flex' : 'hidden') : 'flex'} flex-col w-full md:w-[360px] flex-shrink-0 bg-white border-r border-gray-200`}>

        {/* Header verde WhatsApp */}
        <div className="bg-[#075e54] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar nome={user?.nome} size="sm"/>
            <div>
              <p className="text-sm font-semibold text-white leading-tight">{user?.nome?.split(' ')[0]}</p>
              <p className="text-xs text-emerald-200 capitalize">{user?.tipo}</p>
            </div>
          </div>
          <button onClick={() => navigate(user?.tipo === 'empresa' ? '/empresa' : '/candidato')}
            className="text-emerald-200 hover:text-white text-xs flex items-center gap-1 transition-colors">
            <ArrowLeft size={13}/> Voltar
          </button>
        </div>

        {/* Busca */}
        <div className="px-3 py-2 bg-white border-b border-gray-100">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input
              className="w-full bg-[#f0f2f5] rounded-full pl-8 pr-3 py-2 text-sm focus:outline-none placeholder-gray-400 text-gray-700"
              placeholder="Pesquisar conversa..."
              value={busca} onChange={e => setBusca(e.target.value)}
            />
          </div>
        </div>

        {/* Lista de conversas */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 size={22} className="animate-spin text-[#25D366]"/></div>
          ) : convsFiltradas.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-gray-400 px-6 text-center">
              <MessageCircle size={38} className="mb-3 opacity-30"/>
              <p className="text-sm font-medium">Nenhuma conversa</p>
              <p className="text-xs mt-1 text-gray-400">
                {user?.tipo === 'empresa'
                  ? 'Selecione um candidato no Match para iniciar.'
                  : 'Aguarde uma empresa entrar em contato.'}
              </p>
            </div>
          ) : (
            convsFiltradas.map((conv, i) => (
              <div key={conv.id}>
                <ConvItem conv={conv} ativo={convAtiva?.id === conv.id} meuId={user?.id} onClick={() => abrirConversa(conv)}/>
                {i < convsFiltradas.length - 1 && <div className="border-b border-gray-100 ml-[68px]"/>}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ════ PAINEL DE MENSAGENS ════ */}
      <div className={`${isMobile ? (showLista ? 'hidden' : 'flex') : 'flex'} flex-col flex-1 overflow-hidden`}>

        {!convAtiva ? (
          /* Tela inicial */
          <div className="flex-1 flex flex-col items-center justify-center bg-[#f0f2f5] text-center px-6">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-5 shadow-sm">
              <MessageCircle size={40} className="text-[#25D366]"/>
            </div>
            <h2 className="font-bold text-xl text-gray-700 mb-2">AcreJob Mensagens</h2>
            <p className="text-sm text-gray-400 max-w-xs">Selecione uma conversa na lista ao lado.</p>
          </div>
        ) : (
          <>
            {/* Header conversa */}
            <div className="bg-[#075e54] px-4 py-2.5 flex items-center gap-3">
              {isMobile && (
                <button onClick={() => { setShowLista(true); clearInterval(pollMsgRef.current); }}
                  className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center text-white mr-1">
                  <ArrowLeft size={18}/>
                </button>
              )}
              <Avatar nome={convAtiva.outro_nome} src={convAtiva.outro_avatar}
                online={isOnline(convAtiva.outro_ativo_em)} size="sm"/>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-white truncate">{convAtiva.outro_nome}</p>
                <p className={`text-xs ${isOnline(convAtiva.outro_ativo_em) ? 'text-emerald-200' : 'text-emerald-300/50'}`}>
                  {isOnline(convAtiva.outro_ativo_em) ? '● online agora'
                    : convAtiva.outro_ativo_em ? `visto ${tempoRelativo(convAtiva.outro_ativo_em)}`
                    : 'offline'}
                </p>
              </div>
            </div>

            {/* Área de mensagens com fundo estilo WA */}
            <div className="flex-1 overflow-y-auto px-4 py-3"
              style={{ background: '#e5ddd5' }}>
              {loadMsg ? (
                <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-[#25D366]"/></div>
              ) : mensagens.length === 0 ? (
                <div className="flex justify-center py-8">
                  <span className="bg-white/90 text-gray-500 text-xs px-4 py-2 rounded-full shadow-sm">
                    Nenhuma mensagem. Diga olá! 👋
                  </span>
                </div>
              ) : (
                msgsPorDia(mensagens).map((item, i) =>
                  item.tipo === 'data'
                    ? <SepData key={'d'+i} data={item.data}/>
                    : <Bolha key={item.msg.id} msg={item.msg} isMinha={item.msg.autor_id === user?.id}/>
                )
              )}
              <div ref={msgEndRef}/>
            </div>

            {/* Input de mensagem */}
            <div className="bg-[#f0f2f5] px-3 py-2 flex items-end gap-2">
              <div className="flex-1 bg-white rounded-3xl px-4 py-2.5 shadow-sm min-h-[44px] flex items-center">
                <textarea
                  ref={inputRef}
                  value={texto}
                  rows={1}
                  onChange={e => {
                    setTexto(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                  placeholder="Digite uma mensagem"
                  className="w-full resize-none focus:outline-none text-sm text-gray-800 placeholder-gray-400 max-h-28 leading-relaxed bg-transparent"
                />
              </div>
              <button
                onClick={enviar}
                disabled={!texto.trim() || enviando}
                className="w-11 h-11 bg-[#075e54] hover:bg-[#0a7a6e] disabled:bg-gray-300 text-white rounded-full flex items-center justify-center flex-shrink-0 transition-colors shadow active:scale-95"
              >
                {enviando ? <Loader2 size={18} className="animate-spin"/> : <Send size={18}/>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
